import { Translate } from '@travel/translation';

import {
  ADDITIONAL_INFO,
  AGE,
  COMPANION,
  GENDER,
  GROUP_ID,
  MONTH,
  PURPOSE,
  RATING,
} from 'constants/searchArea';

import type {
  AvailableMonths,
  ProviderFiltersResponse,
  ReviewTopic,
} from 'store/providerFilters/types';
import type { FilterGroup } from '../type';

export function createFilterGroups(filterData: ProviderFiltersResponse | undefined): FilterGroup[] {
  if (!filterData) return [];

  const groups: FilterGroup[] = [];

  if (Array.isArray(filterData.topics) && filterData.topics.length) {
    groups.push({
      groupId: GROUP_ID.TOPICS,
      groupName: <Translate id="Review.Filter.Suggested.Title" />,
      features: filterData.topics.map((topic: ReviewTopic) => ({
        featureId: topic.topicName,
        name: topic.topicName,
        count: topic.count,
        selected: false,
      })),
    });
  }

  if (filterData.availableByMonths?.length) {
    groups.push({
      groupId: GROUP_ID.AVAILABLE_BY_MONTHS,
      groupName: <Translate id="Review.Filter.StayMonth.Title" />,
      features: filterData.availableByMonths.map((month: AvailableMonths) => ({
        featureId: month.value,
        name: MONTH[month.value as keyof typeof MONTH],
        selected: false,
        isDisabled: !month.isAvailable,
      })),
    });
  }

  if (filterData.ratings?.length) {
    const reversedRatings = filterData.ratings.slice().reverse();

    groups.push({
      groupId: GROUP_ID.RATINGS,
      groupName: <Translate id="Review.Filter.Rating.Title" />,
      features: reversedRatings.map((value: string) => ({
        featureId: value,
        code: value,
        name: String(RATING[value as keyof typeof RATING]),
        selected: false,
      })),
    });
  }

  if (filterData.purposes?.length) {
    groups.push({
      groupId: GROUP_ID.PURPOSES,
      groupName: <Translate id="Review.Filter.TripPurpose.Title" />,
      features: filterData.purposes.map((value: string) => ({
        featureId: value,
        name: PURPOSE[value as keyof typeof PURPOSE],
        selected: false,
      })),
    });
  }

  if (filterData.companions?.length) {
    groups.push({
      groupId: GROUP_ID.COMPANIONS,
      groupName: <Translate id="Review.Filter.Companion.Title" />,
      features: filterData.companions.map((value: string) => ({
        featureId: value,
        name: COMPANION[value as keyof typeof COMPANION],
        selected: false,
      })),
    });
  }

  if (filterData.additionInformation?.length) {
    groups.push({
      groupId: GROUP_ID.ADDITION_INFORMATION,
      groupName: <Translate id="Review.Filter.AdditionalInfo.Title" />,
      features: filterData.additionInformation.map((value: string) => ({
        featureId: value,
        name: ADDITIONAL_INFO[value as keyof typeof ADDITIONAL_INFO],
        selected: false,
      })),
    });
  }

  if (filterData.ageRanges?.length) {
    groups.push({
      groupId: GROUP_ID.AGE_RANGES,
      groupName: <Translate id="Review.Filter.Age.Title" />,
      features: filterData.ageRanges.map((value: string) => ({
        featureId: value,
        name: AGE[value as keyof typeof AGE],
        selected: false,
      })),
    });
  }

  if (filterData.genders?.length) {
    groups.push({
      groupId: GROUP_ID.GENDERS,
      groupName: <Translate id="Review.Filter.Gender.Title" />,
      features: filterData.genders.map((value: string) => ({
        featureId: value,
        name: GENDER[value as keyof typeof GENDER],
        selected: false,
      })),
    });
  }

  return groups;
}
